/**
 * Раздел «Население» страницы «Аналитика» (redesign по макету): заголовок
 * секции с линией и иконкой + ряд стат-карточек (StatCard) +
 * «Динамика численности населения» (линейный график факт/прогноз на
 * shadcn chart) и «Социальные группы» (строки с барами) + подраздел
 * «Скрининг территории»: «Демография» (столбцы по возрастам),
 * «Естественное движение населения» (цветные бары) и «Рынок труда»
 * (три пары столбцов 2025/2026).
 *
 * Источник данных — гибрид: демо-КАТО (HARD_DATA_KATOS)
 * → мок hardData/analyticsPopulation.json; остальные КАТО (по выбранному
 * в сайдбаре) — один запрос /api/public/analytics/population (DP-125,
 * US-04.2): plashka → стат-карточки, population_dynamics → «Динамика
 * численности» (факт/прогноз по флагу forecast), social_groups →
 * «Социальные группы», demography → «Демография», natural_movement →
 * «Естественное движение», labor_market → «Рынок труда» (последние
 * два года по каждому показателю).
 */
import { TrendingDown, TrendingUp, UsersRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Line,
  LineChart,
  Pie,
  PieChart,
  XAxis,
} from 'recharts';

import { StatCard } from '../../../components/widgets/StatCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '../../../components/ui/chart';
import analyticsPopulation from '../../../hardData/analyticsPopulation.json';
import { cn } from '../../../lib/cn';
import { getAnalyticsPopulation } from '../../../requests/analytics.request';
import { useAnalyticsSection } from '../useAnalyticsSection';
import type {
  LaborMarketYear,
  PopulationAnalyticsResponse,
} from '../../../types/analytics';
import type { StatCardFootnote, StatCardTrend } from '../../../components/widgets/StatCard';

const fmt = (v: number): string => Intl.NumberFormat('ru-RU').format(v);
const fmt1 = (v: number): string =>
  Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 }).format(v);

/** Цвет бара «Естественного движения» — токены темы по ключу из мока. */
const MOVEMENT_BAR: Record<string, string> = {
  green: 'bg-brand-600',
  dark: 'bg-text',
  blue: 'bg-chart-blue',
};

// Цвета линий графика = токены темы (brand-600 / chart-green из
// tailwind.config.js) — recharts принимает цвет строкой.
const FACT_COLOR = '#2C9C42';
const FORECAST_COLOR = '#A3CE63';

/** Палитра сегментов «Демографии» для populationByAge (оттенки chart-blue). */
const AGE_COLORS = ['#AAB8F0', '#8FA3F2', '#6D7FE3', '#4A63E7', '#2B4FC7'];

/** Пары цветов столбцов «Рынка труда» (прошлый/текущий год) — как в моке. */
const LABOR_COLORS: Record<string, { prev: string; current: string }> = {
  economicallyActive: { prev: '#A9CDA4', current: '#3D9635' },
  employed: { prev: '#AAB8F0', current: '#6D7FE3' },
  avgSalary: { prev: '#C98BEF', current: '#A620F0' },
};

interface YearTickProps {
  x?: number;
  y?: number;
  payload?: { value?: number | string };
}

/** Подпись года на оси X: текущий год — синим жирным, как на макете. */
const YearTick = ({
  x,
  y,
  payload,
  currentYear,
}: YearTickProps & { currentYear?: number | null }) => {
  const isCurrent = payload?.value === currentYear;
  return (
    <text
      x={x}
      y={(y ?? 0) + 14}
      textAnchor="middle"
      fontSize={13}
      fontWeight={isCurrent ? 800 : 500}
      fill={isCurrent ? '#4A63E7' : '#8A968D'}
    >
      {payload?.value}
    </text>
  );
};

/** Год под столбцами «Рынка труда»: текущий — тёмным жирным. */
const LaborYearTick = ({
  x,
  y,
  payload,
  currentYear,
}: YearTickProps & { currentYear?: number | null }) => {
  const isCurrent = payload?.value === currentYear;
  return (
    <text
      x={x}
      y={(y ?? 0) + 14}
      textAnchor="middle"
      fontSize={13}
      fontWeight={isCurrent ? 700 : 500}
      fill={isCurrent ? '#1F2A24' : '#8A968D'}
    >
      {payload?.value}
    </text>
  );
};

interface CardVM {
  key: string;
  value: string;
  unit: string | null;
  danger?: boolean;
  trend: StatCardTrend | null;
  footnotes?: StatCardFootnote[];
}

interface DynamicsVM {
  currentYear: number | null;
  currentValue: number | null;
  trendText: string | null;
  trendDown: boolean;
  /** «к {прошлый год} году» — подпись рядом с трендом. */
  footnote: string | null;
  points: Array<{ year: number; fact: number | null; forecast: number | null }>;
}

interface GroupRowVM {
  key: string;
  label: string;
  value: string;
  barPercent: number;
}

interface MovementRowVM {
  key: string;
  label: string;
  value: string;
  barPercent: number;
  color: string;
}

interface LaborChartVM {
  key: string;
  name: string;
  colors: { prev: string; current: string };
  bars: Array<{ year: number; value: number; label: string }>;
}

interface PopulationSectionProps {
  /** Режим витрины «Все» (US-04.1): только ключевые виджеты —
   * «Демография» и «Естественное движение населения» (FR-2.2). */
  summary?: boolean;
}

export const PopulationSection = ({
  summary = false,
}: PopulationSectionProps = {}) => {
  const { t, i18n } = useTranslation();
  const isKz = i18n.resolvedLanguage === 'kz';
  const { screening } = analyticsPopulation;

  const { data, loading, isHardKato } =
    useAnalyticsSection<PopulationAnalyticsResponse>(
      'population',
      getAnalyticsPopulation,
    );

  // --- Вью-модели: мок для демо-КАТО, /analytics/population для остальных ---

  let cards: CardVM[];
  let dynamicsVM: DynamicsVM;
  let groupRows: GroupRowVM[];

  if (isHardKato) {
    const { dynamics, socialGroups } = analyticsPopulation;
    cards = analyticsPopulation.cards.map((card) => {
      // Пустой footer в моке — сноска не показывается (годы убраны).
      const footerText = isKz ? card.footer.kz : card.footer.ru;
      return {
        key: card.key,
        value:
          card.unit === 'percent' ? `${fmt(card.value)}%` : fmt(card.value),
        unit: card.unit === 'people' ? t('units.people') : null,
        danger: card.danger,
        trend:
          card.trend != null
            ? {
                text: `${card.trend > 0 ? '+' : ''}${card.trend}%`,
                down: card.trend < 0,
              }
            : null,
        footnotes: footerText
          ? [{ text: footerText, danger: card.danger }]
          : [],
      };
    });
    dynamicsVM = {
      currentYear: dynamics.currentYear,
      currentValue: dynamics.current.value,
      trendText: `+${dynamics.current.trend}`,
      trendDown: false,
      footnote: t('analytics.population.dynamics.vsYear', {
        year: dynamics.currentYear - 1,
      }),
      points: dynamics.points,
    };
    groupRows = socialGroups.rows.map((row) => ({
      key: row.key,
      label: isKz ? row.name.kz : row.name.ru,
      value: row.value,
      barPercent: row.barPercent,
    }));
  } else {
    const plashka = data?.plashka;
    const groups = data?.social_groups ?? null;
    const points = data?.population_dynamics ?? [];

    const currentYear = data?.year ?? null;
    const currentValue =
      points.find((p) => p.year === currentYear)?.value ??
      plashka?.population ??
      null;
    const prevValue =
      currentYear !== null
        ? (points.find((p) => p.year === currentYear - 1)?.value ?? null)
        : null;
    const trendPct =
      currentValue !== null && prevValue !== null && prevValue !== 0
        ? Math.round(((currentValue - prevValue) / prevValue) * 1000) / 10
        : null;
    const trendVM: StatCardTrend | null =
      trendPct !== null
        ? { text: `${trendPct > 0 ? '+' : ''}${trendPct}%`, down: trendPct < 0 }
        : null;

    const population = plashka?.population ?? null;
    const econActive = plashka?.economically_active_population ?? null;
    const youth = plashka?.youth ?? null;
    const unemployed = plashka?.unemployed ?? groups?.unemployed ?? null;
    const saldo =
      plashka?.saldo_migration ??
      data?.natural_movement?.saldo_migration ??
      null;
    // Уровень безработицы — из плашки; нет в плашке → доля от
    // экономически активных.
    const unemploymentRate =
      plashka?.unemployment_rate ??
      (unemployed !== null && econActive !== null && econActive !== 0
        ? (unemployed / econActive) * 100
        : null);

    const pctOfPopulation = (v: number | null): StatCardFootnote[] =>
      v !== null && population !== null && population !== 0
        ? [{ text: `${fmt1((v / population) * 100)} %` }]
        : [];

    cards = [
      {
        key: 'population',
        value: population !== null ? fmt(population) : '—',
        unit: t('units.people'),
        trend: trendVM,
      },
      {
        key: 'economicallyActive',
        value: econActive !== null ? fmt(econActive) : '—',
        unit: t('units.people'),
        trend: null,
        footnotes: pctOfPopulation(econActive),
      },
      {
        key: 'youth',
        value: youth !== null ? fmt(youth) : '—',
        unit: t('units.people'),
        trend: null,
        footnotes: pctOfPopulation(youth),
      },
      {
        key: 'unemployment',
        value: unemploymentRate !== null ? `${fmt1(unemploymentRate)}%` : '—',
        // «712 человек» — на строке единиц, чтобы лежало на одной линии
        // с «человек» соседних карточек.
        unit:
          unemployed !== null
            ? `${fmt(unemployed)} ${t('units.people')}`
            : null,
        trend: null,
        footnotes: [],
      },
      {
        key: 'saldoMigration',
        value: saldo !== null ? fmt(saldo) : '—',
        unit: t('units.people'),
        danger: saldo !== null && saldo < 0,
        trend: null,
        footnotes:
          saldo !== null
            ? [
                {
                  text: t(saldo < 0 ? 'units.outflow' : 'units.inflow'),
                  danger: saldo < 0,
                },
              ]
            : [],
      },
    ];

    dynamicsVM = {
      currentYear,
      currentValue,
      trendText: trendVM?.text ?? null,
      trendDown: trendVM?.down ?? false,
      footnote:
        currentYear !== null
          ? t('analytics.population.dynamics.vsYear', {
              year: currentYear - 1,
            })
          : null,
      // Факт/прогноз — по флагу forecast (fallback — годы после года
      // ответа: staging пока не проставляет флаг у прогнозных значений);
      // последняя фактическая точка перед прогнозом дублируется в
      // прогнозной серии, чтобы пунктир начинался от неё без разрыва.
      points: (() => {
        const isForecast = (p: (typeof points)[number]): boolean =>
          p.forecast || (currentYear !== null && p.year > currentYear);
        return points.map((p, index) => {
          const next = points[index + 1];
          return {
            year: p.year,
            fact: isForecast(p) ? null : p.value,
            forecast:
              isForecast(p) || (next !== undefined && isForecast(next))
                ? p.value
                : null,
          };
        });
      })(),
    };

    // «Социальные группы» — фиксированный состав по постановке:
    // молодёжь / наёмные / самозанятые / безработные / работающие
    // пенсионеры / АСП / пенсионеры / многодетные / инвалидность.
    // Источник — social_groups ответа; группа без данных скрывается
    // (замечание ревью: строки с «—» не показываем).
    const groupDefs: Array<[string, number | null]> = [
      ['youth', groups?.youth ?? youth],
      ['hired', groups?.hired ?? null],
      ['selfEmployed', groups?.self_employed ?? null],
      ['unemployed', groups?.unemployed ?? unemployed],
      ['workingPensioners', groups?.working_elderly ?? null],
      ['aspRecipients', groups?.tsa_recipients ?? null],
      ['pensioners', groups?.pensioners ?? null],
      ['multiChildFamilies', groups?.large_families ?? null],
      ['disabled', groups?.disabled ?? null],
    ];
    const knownGroups = groupDefs.filter(
      (def): def is [string, number] => def[1] !== null,
    );
    const maxGroup = Math.max(1, ...knownGroups.map(([, v]) => v));
    groupRows = knownGroups.map(([key, v]) => ({
      key,
      label: t(`analytics.population.socialGroups.rows.${key}`),
      value: fmt(v),
      barPercent: Math.max(2, (v / maxGroup) * 100),
    }));
  }

  const chartConfig = {
    fact: {
      label: t('analytics.population.dynamics.fact'),
      color: FACT_COLOR,
    },
    forecast: {
      label: t('analytics.population.dynamics.forecast'),
      color: FORECAST_COLOR,
    },
  } satisfies ChartConfig;

  // «Демография»: демо-КАТО — мок; остальные — demography ответа
  // (возрастные группы 0-5 … 63+).
  const demographySegments: Array<{
    group: string;
    value: number;
    color: string;
  }> = isHardKato
    ? screening.demography.bars
    : (data?.demography ?? []).flatMap((row, index) =>
        row.population !== null
          ? [
              {
                group: row.age,
                value: row.population,
                color: AGE_COLORS[index % AGE_COLORS.length],
              },
            ]
          : [],
      );
  // Медианный возраст убран: его нет ни в постановках (US-02 / US-04.2),
  // ни в данных (statbase хранит только справочник возрастных групп).

  // «Естественное движение»: демо-КАТО — мок; остальные —
  // natural_movement ответа (нет поля → строка скрывается,
  // прирост/сальдо досчитываются из компонент).
  const movementRows: MovementRowVM[] = isHardKato
    ? screening.naturalMovement.rows.map((row) => ({
        key: row.key,
        label: isKz ? row.name.kz : row.name.ru,
        value: row.value,
        barPercent: row.barPercent,
        color: row.color,
      }))
    : (() => {
        const d = data?.natural_movement;
        if (!d) return [];
        const natural =
          d.natural_increase ??
          (d.births !== null && d.deaths !== null
            ? d.births - d.deaths
            : null);
        const saldo =
          d.saldo_migration ??
          (d.immigration !== null && d.emigration !== null
            ? d.immigration - d.emigration
            : null);
        const defs: Array<[string, number | null, string]> = [
          ['birthRate', d.births, 'green'],
          ['deathRate', d.deaths, 'dark'],
          ['naturalGrowth', natural, 'blue'],
          ['arrived', d.immigration, 'blue'],
          ['departed', d.emigration, 'blue'],
          ['saldoMigration', saldo, 'blue'],
        ];
        const max = Math.max(1, ...defs.map(([, v]) => Math.abs(v ?? 0)));
        return defs.flatMap(([key, v, color]) =>
          v === null
            ? []
            : [
                {
                  key,
                  label: t(
                    `analytics.population.screening.naturalMovement.rows.${key}`,
                  ),
                  value: fmt(v),
                  barPercent: Math.max(2, (Math.abs(v) / max) * 100),
                  color,
                },
              ],
        );
      })();
  const movementUnit = isHardKato
    ? isKz
      ? screening.naturalMovement.unit.kz
      : screening.naturalMovement.unit.ru
    : t('units.people');

  // «Рынок труда»: демо-КАТО — мок; остальные — labor_market ответа
  // (последние два года по каждому показателю, как на макете).
  // Нет значений → чарт скрыт.
  const laborRows = data?.labor_market ?? [];
  const laborCurrentYear = isHardKato
    ? screening.laborMarket.currentYear
    : laborRows.length > 0
      ? laborRows[laborRows.length - 1].year
      : null;
  const laborCharts: LaborChartVM[] = isHardKato
    ? screening.laborMarket.charts.map((chart) => ({
        key: chart.key,
        name: isKz ? chart.name.kz : chart.name.ru,
        colors: chart.colors,
        bars: chart.bars,
      }))
    : (() => {
        const defs: Array<
          [string, (row: LaborMarketYear) => number | null]
        > = [
          ['economicallyActive', (row) => row.economically_active],
          ['employed', (row) => row.employed],
          ['avgSalary', (row) => row.avg_salary],
        ];
        return defs.flatMap(([key, pick]) => {
          const bars = laborRows
            .flatMap((row) => {
              const value = pick(row);
              return value !== null
                ? [{ year: row.year, value, label: fmt(value) }]
                : [];
            })
            .slice(-2);
          if (bars.length === 0) return [];
          return [
            {
              key,
              name: t(
                `analytics.population.screening.laborMarket.charts.${key}`,
              ),
              colors: LABOR_COLORS[key],
              bars,
            },
          ];
        });
      })();

  return (
    <section className="space-y-4" data-testid="analytics-population">
      <div className="flex items-center gap-4">
        <h2 className="shrink-0 text-xl font-extrabold">
          {t('analytics.population.title')}
        </h2>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
        <UsersRound
          className="size-6 shrink-0 text-brand-600"
          strokeWidth={1.8}
          aria-hidden="true"
        />
      </div>

      {/* Пока грузятся данные с бэка — лоадер вместо виджетов. */}
      {loading ? (
        <BlockLoader />
      ) : (
        <>

      {!summary && (
      <div className="grid gap-3.5 sm:grid-cols-2 xl:grid-cols-5">
        {cards.map((card) => (
          <StatCard
            key={card.key}
            testId={`analytics-card-${card.key}`}
            title={t(`analytics.population.cards.${card.key}`)}
            value={card.value}
            unit={card.unit}
            danger={card.danger}
            trend={card.trend}
            footnotes={card.footnotes}
          />
        ))}
      </div>
      )}

      {!summary && (
      <div className="grid gap-3.5 xl:grid-cols-2">
        <Card className="shadow-card">
          <CardHeader className="flex-row items-baseline justify-between space-y-0">
            <CardTitle>{t('analytics.population.dynamics.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div data-testid="analytics-population-dynamics">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl border border-surface-border px-4 py-3.5">
                <span
                  className="text-3xl font-extrabold tabular-nums text-chart-indigo"
                  data-testid="analytics-dynamics-value"
                >
                  {dynamicsVM.currentValue !== null
                    ? fmt(dynamicsVM.currentValue)
                    : '—'}
                </span>
                <span className="text-sm text-text-muted">
                  {t('units.people')}
                </span>
                {dynamicsVM.trendText && (
                  <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-brand-100 bg-surface px-3 py-1 text-sm font-bold text-brand-600">
                    {dynamicsVM.trendDown ? (
                      <TrendingDown className="size-4" />
                    ) : (
                      <TrendingUp className="size-4" />
                    )}
                    {dynamicsVM.trendText}
                  </span>
                )}
                {dynamicsVM.footnote && (
                  <span className="text-sm text-text-muted">
                    {dynamicsVM.footnote}
                  </span>
                )}
              </div>

              <ChartContainer config={chartConfig} className="mt-4 h-56 w-full">
                {/* Отступы по краям — чтобы подписи крайних годов
                    (2023 / 2029) не обрезались (замечание дизайн-ревью). */}
                <LineChart
                  data={dynamicsVM.points}
                  margin={{ top: 8, right: 20, bottom: 0, left: 20 }}
                >
                  <CartesianGrid vertical={false} stroke="#E4E9E5" />
                  <XAxis
                    dataKey="year"
                    tickLine={false}
                    axisLine={false}
                    interval={0}
                    padding={{ left: 8, right: 8 }}
                    tick={<YearTick currentYear={dynamicsVM.currentYear} />}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent hideLabel />}
                  />
                  <Line
                    dataKey="fact"
                    type="linear"
                    stroke={FACT_COLOR}
                    strokeWidth={2.5}
                    dot={{ r: 6, strokeWidth: 0, fill: FACT_COLOR }}
                    activeDot={{ r: 7 }}
                  />
                  <Line
                    dataKey="forecast"
                    type="linear"
                    stroke={FORECAST_COLOR}
                    strokeWidth={2.5}
                    strokeDasharray="2 6"
                    dot={{ r: 6, strokeWidth: 0, fill: FORECAST_COLOR }}
                    activeDot={{ r: 7 }}
                  />
                </LineChart>
              </ChartContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="flex-row items-baseline justify-between space-y-0">
            <CardTitle>
              {t('analytics.population.socialGroups.title')}
            </CardTitle>
            <span className="text-sm text-text-faint">{t('units.people')}</span>
          </CardHeader>
          <CardContent>
            {/* Все группы без данных → «Нет данных» вместо пустого списка. */}
            {groupRows.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
            <ul data-testid="analytics-social-groups">
              {groupRows.map((row) => (
                <li
                  key={row.key}
                  data-testid={`analytics-social-group-${row.key}`}
                  className="flex items-center gap-4 py-3 text-sm"
                >
                  <span className="w-48 shrink-0 truncate font-medium">
                    {row.label}
                  </span>
                  <span className="h-4 min-w-0 flex-1 overflow-hidden rounded-full bg-surface-muted">
                    <span
                      className="block h-full rounded-full bg-chart-blue"
                      style={{ width: `${row.barPercent}%` }}
                    />
                  </span>
                  <span className="w-14 shrink-0 text-right font-extrabold tabular-nums">
                    {row.value}
                  </span>
                </li>
              ))}
            </ul>
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {!summary && (
      <div className="flex items-center gap-4 pt-2">
        <h3 className="shrink-0 text-lg font-extrabold">
          {t('analytics.population.screening.title')}
        </h3>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
      </div>
      )}

      <div className="grid gap-3.5 xl:grid-cols-2">
        <Card className="shadow-card">
          <CardHeader className="flex-row items-baseline justify-between space-y-0">
            <CardTitle>
              {t('analytics.population.screening.demography.title')}
            </CardTitle>
            <span className="text-sm text-text-faint">
              {isKz
                ? screening.demography.unit.kz
                : screening.demography.unit.ru}
            </span>
          </CardHeader>
          <CardContent>
            <div data-testid="analytics-demography">
              {demographySegments.length === 0 ? (
                <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
              ) : (
                <>
                  {/* Pie вместо bar-чарта (замечание дизайн-ревью):
                      доли возрастных групп, легенда «значение · группа лет». */}
                  <div className="relative mx-auto mt-2 w-full max-w-[240px]">
                    <ChartContainer
                      config={Object.fromEntries(
                        demographySegments.map((bar) => [
                          bar.group,
                          {
                            label: `${bar.group} ${t('analytics.population.screening.demography.years')}`,
                            color: bar.color,
                          },
                        ]),
                      )}
                      className="aspect-square"
                    >
                      <PieChart>
                        <ChartTooltip
                          cursor={false}
                          content={<ChartTooltipContent hideLabel />}
                        />
                        <Pie
                          data={demographySegments}
                          dataKey="value"
                          nameKey="group"
                          outerRadius="98%"
                          strokeWidth={2}
                        >
                          {demographySegments.map((bar) => (
                            <Cell key={bar.group} fill={bar.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ChartContainer>
                  </div>
                  <ul
                    className="mt-4 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-[15px]"
                    data-testid="analytics-demography-legend"
                  >
                    {demographySegments.map((bar) => (
                      <li key={bar.group} className="flex items-center gap-2">
                        <span
                          className="font-bold tabular-nums"
                          style={{ color: bar.color }}
                        >
                          {fmt(bar.value)}
                        </span>
                        <span
                          className="size-2 shrink-0 rounded-sm"
                          style={{ backgroundColor: bar.color }}
                          aria-hidden="true"
                        />
                        <span>
                          {bar.group}{' '}
                          {t('analytics.population.screening.demography.years')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-card">
          <CardHeader className="flex-row items-baseline justify-between gap-4 space-y-0">
            <CardTitle>
              {t('analytics.population.screening.naturalMovement.title')}
            </CardTitle>
            <span className="shrink-0 text-sm text-text-faint">
              {movementUnit}
            </span>
          </CardHeader>
          <CardContent>
            {movementRows.length === 0 ? (
              <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
            ) : (
              <ul data-testid="analytics-natural-movement">
                {movementRows.map((row) => (
                  <li
                    key={row.key}
                    data-testid={`analytics-movement-${row.key}`}
                    className="flex items-center gap-4 border-b border-surface-border py-4 text-sm last:border-b-0 last:pb-0"
                  >
                    <span className="w-52 shrink-0 truncate font-medium">
                      {row.label}
                    </span>
                    <span className="h-9 min-w-0 flex-1 overflow-hidden rounded-lg bg-surface-muted">
                      <span
                        className={cn(
                          'block h-full rounded-lg',
                          MOVEMENT_BAR[row.color] ?? 'bg-chart-blue',
                        )}
                        style={{ width: `${row.barPercent}%` }}
                      />
                    </span>
                    <span className="w-14 shrink-0 text-right text-base font-extrabold tabular-nums">
                      {row.value}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {!summary && (
      <Card className="shadow-card">
        <CardHeader>
          <CardTitle>
            {t('analytics.population.screening.laborMarket.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {laborCharts.length === 0 ? (
            <p data-testid="block-placeholder" className="text-sm lowercase text-text-muted">{t('common.noData')}</p>
          ) : (
            <div
              className="grid gap-8 sm:grid-cols-3"
              data-testid="analytics-labor-market"
            >
              {laborCharts.map((chart) => (
                <div
                  key={chart.key}
                  data-testid={`analytics-labor-${chart.key}`}
                >
                  {/* Легенда по центру своей колонки (замечание дизайн-ревью).
                      min-h на две строки: легенды разной высоты не должны
                      сдвигать графики — бары и подписи годов по всем трём
                      чартам лежат на одних линиях. */}
                  <p className="flex min-h-12 items-center justify-center gap-2 text-center text-[15px] font-bold leading-snug">
                    <span
                      className="size-2 shrink-0 rounded-sm"
                      style={{ backgroundColor: chart.colors.current }}
                      aria-hidden="true"
                    />
                    {chart.name}
                  </p>
                  <ChartContainer
                    config={{
                      value: {
                        label: chart.name,
                        color: chart.colors.current,
                      },
                    }}
                    className="mt-2 h-56 w-full"
                  >
                    <BarChart
                      data={chart.bars}
                      margin={{ top: 24, right: 16, bottom: 0, left: 16 }}
                    >
                      <CartesianGrid vertical={false} stroke="#E4E9E5" />
                      <XAxis
                        dataKey="year"
                        tickLine={false}
                        axisLine={false}
                        interval={0}
                        tick={<LaborYearTick currentYear={laborCurrentYear} />}
                      />
                      <Bar dataKey="value" radius={8} barSize={72}>
                        <LabelList
                          dataKey="label"
                          position="top"
                          offset={8}
                          fill="#1F2A24"
                          fontSize={13}
                          fontWeight={700}
                        />
                        {chart.bars.map((bar) => (
                          <Cell
                            key={bar.year}
                            fill={
                              bar.year === laborCurrentYear
                                ? chart.colors.current
                                : chart.colors.prev
                            }
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ChartContainer>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
        </>
      )}
    </section>
  );
};
